import { useEffect, useState } from 'react';

import { Drawer } from 'antd';
import { FileText, LoaderCircle, Presentation } from 'lucide-react';

import { useDevice } from '../../../hooks/useDevice';
import { pptApi } from '../api';
import { STATUS_LABELS } from '../constants';
import { formatTime } from '../helpers';

import styles from '../index.module.scss';

/** “我的 PPT”：按设备标识（X-Guest-Id）列出这台设备上做过的 PPT，无需登录 */
export default function HistoryDrawer({ open, currentUid, onClose, onSelect }) {
    const { isMobile } = useDevice();
    const [state, setState] = useState({ loading: false, items: [], error: false });

    useEffect(() => {
        if (!open) return undefined;
        let active = true;
        setState((current) => ({ ...current, loading: true, error: false }));
        pptApi
            .history()
            .then((data) => {
                if (active) setState({ loading: false, items: data?.items || [], error: false });
            })
            .catch(() => {
                if (active) setState({ loading: false, items: [], error: true });
            });
        return () => {
            active = false;
        };
    }, [open]);

    return (
        <Drawer
            title="我的 PPT"
            open={open}
            onClose={onClose}
            width={isMobile ? '100%' : 420}
            className={styles.historyDrawer}
        >
            <p className={styles.historyTip}>
                记录跟随这台设备的浏览器，换设备或清除浏览器数据后将看不到。
            </p>
            {state.loading ? (
                <div className={styles.loadingBlock} role="status">
                    <LoaderCircle className={styles.spin} size={18} aria-hidden />
                    正在加载…
                </div>
            ) : state.error ? (
                <p className={styles.emptyText}>记录加载失败，请稍后重试。</p>
            ) : state.items.length === 0 ? (
                <p className={styles.emptyText}>还没有生成过 PPT。</p>
            ) : (
                <ul className={styles.historyList}>
                    {state.items.map((item) => {
                        const Icon = item.inputType === 'file' ? FileText : Presentation;
                        const pages = item.renderedPages || item.plannedPages;
                        return (
                            <li key={item.uid}>
                                <button
                                    type="button"
                                    className={styles.historyItem}
                                    aria-current={item.uid === currentUid ? 'true' : undefined}
                                    onClick={() => onSelect(item.uid)}
                                >
                                    <Icon size={18} aria-hidden />
                                    <span className={styles.historyBody}>
                                        <strong>
                                            {item.title || item.fileName || '未命名演示文稿'}
                                        </strong>
                                        <small>
                                            {[
                                                formatTime(item.createTime),
                                                item.templateName,
                                                pages ? `${pages} 页` : null,
                                            ]
                                                .filter(Boolean)
                                                .join(' · ')}
                                        </small>
                                    </span>
                                    <span className={styles.statusTag} data-status={item.status}>
                                        {STATUS_LABELS[item.status] || item.status}
                                    </span>
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
        </Drawer>
    );
}
